import './CollectionForm.scss'

import { HDSButton, HDSIcon, HDSInput, HDSTextarea } from '@here/hds-react-components'
import CloseButton from 'components/App/components/CloseButton'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { useAppDispatch } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import React, { CSSProperties, useState } from 'react'

import { ViewType } from '../Collections'
import { CollectionFormPayload } from '../data/api/collections'
import { createCollection, editCollection } from '../data/reducer'
import { CollectionCategory, getCollectionCategoryUrlPath } from '../util/collection-category'

export interface CollectionFormProps {
  view: ViewType
  collection?: Collection
  onActionClick: (action: Action) => void
}

export enum Action {
  close = 'close',
}

export interface Collection {
  name: string
  description?: string
  id: string
  category?: CollectionCategory
  placesCount: number
}

export default function CollectionForm({
  view,
  collection,
  onActionClick,
}: CollectionFormProps): JSX.Element {
  const dispatch = useAppDispatch()
  const [name, setName] = useState(
    view === ViewType.collectionsEdit && collection ? collection.name : undefined
  )
  const [description, setDescription] = useState(
    view === ViewType.collectionsEdit && collection ? collection.description : undefined
  )
  const [selectedCategory, setSelectedCategory] = useState(
    view === ViewType.collectionsEdit && collection
      ? collection.category
      : CollectionCategory.category1
  )

  const title =
    view === ViewType.collectionsAdd
      ? i18n('collections.collection-list.actions.new-collection')
      : i18n('collections.collection-list.actions.edit-collection')

  const places = i18n('collections.collection-list.places')
  const submitButtonLabel =
    view === ViewType.collectionsAdd
      ? i18n('collections.collection-form.label.create-collection')
      : i18n('collections.collection-form.label.save-changes')

  const handleSubmit = (): void => {
    if (name === undefined) return
    const newCollection: CollectionFormPayload = {
      id: '',
      name,
      portraitImageUrl: selectedCategory,
      type: 'collection',
    }
    if (description !== undefined) newCollection.description = description

    if (view === ViewType.collectionsAdd) {
      dispatch(createCollection(newCollection))
    } else if (view === ViewType.collectionsEdit) {
      if (collection?.id === undefined || collection?.id === null) return

      newCollection.id = collection?.id
      dispatch(editCollection(newCollection))
    }
    onActionClick(Action.close)
  }

  return (
    <div className="CollectionForm">
      <div className="CollectionForm__header">
        <div className="CollectionForm__title">
          <span>{title}</span>
          <CloseButton onClick={() => onActionClick(Action.close)} />
        </div>
        <div className="CollectionForm_subtitle">
          {places}: {view === ViewType.collectionsEdit && collection ? collection.placesCount : 0}
        </div>
      </div>
      <ScrollableContainer>
        <div className="CollectionForm__content">
          <HDSInput
            charCounter
            minlength={1}
            maxlength={60}
            value={name}
            label={i18n('collections.collection-form.name-label')}
            placeholder={i18n('collections.collection-form.name-placeholder')}
            onInput={(e) => setName(e.detail.value.toString())}
          />

          <HDSTextarea
            charCounter
            className="CollectionForm__input-description"
            maxlength={200}
            value={description}
            label={i18n('collections.collection-form.desc-label')}
            placeholder={i18n('collections.collection-form.desc-placeholder')}
            rows={4}
            secondaryLabel={i18n('collections.collection-form.label.optional')}
            onInput={(e) => setDescription(e.detail.value.toString())}
          />

          <div className="CollectionForm_subtitle">
            {i18n('collections.collection-form.choose-color-label')}
          </div>
          <div className="CollectionForm_color-list">
            {Object.values(CollectionCategory).map((category, index) => (
              <div
                key={index}
                className="CollectionForm_color"
                onClick={() => setSelectedCategory(category)}
                style={
                  {
                    backgroundImage: `url("${getCollectionCategoryUrlPath(category)}")`,
                  } as CSSProperties
                }
              >
                {selectedCategory === category && (
                  <HDSIcon
                    className="CollectionForm_color-selected-icon"
                    name="confirm"
                    category="core-ui"
                    iconStyle="solid"
                    size="24px"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </ScrollableContainer>
      <div className="CollectionForm__footer">
        <HDSButton
          disabled={name === undefined || name.length === 0}
          onClick={handleSubmit}
          width="100%"
        >
          {submitButtonLabel}
        </HDSButton>
      </div>
    </div>
  )
}
